import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { sendSlackMessageTracked } from "../../../models/notifications";
import ModernPricingView from "../../pricing/modernPricingView";

// ─── Tokens ──────────────────────────────────────────────────────────────────
const GREEN = "#22c55e";
const DARK  = "#0a0a0a";
const font  = "'Inter', system-ui, sans-serif";

// ─── Section primitives ───────────────────────────────────────────────────────
function Section({ children, bg = "#fff", style = {} }) {
    return (
        <section style={{ background: bg, padding: "80px 24px", fontFamily: font, ...style }}>
            <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
                {children}
            </div>
        </section>
    );
}

function Eyebrow({ children, light = false }) {
    return (
        <p style={{
            fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em",
            textTransform: "uppercase", marginBottom: "12px",
            color: light ? "#4ade80" : "#16a34a",
        }}>
            {children}
        </p>
    );
}

function SectionHeading({ children, light = false, center = false }) {
    return (
        <h2 style={{
            fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 800, lineHeight: 1.15,
            color: light ? "#fff" : "#111827",
            textAlign: center ? "center" : "left",
            marginBottom: "16px",
        }}>
            {children}
        </h2>
    );
}

function Body({ children, light = false, center = false }) {
    return (
        <p style={{
            fontSize: "15px", color: light ? "rgba(255,255,255,0.6)" : "#6b7280",
            lineHeight: 1.75, textAlign: center ? "center" : "left",
        }}>
            {children}
        </p>
    );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ number, label, light = false }) {
    return (
        <div style={{
            padding: "28px 24px", borderRadius: "14px",
            background: light ? "rgba(255,255,255,0.06)" : "#f9fafb",
            border: light ? "1px solid rgba(255,255,255,0.1)" : "1px solid #f3f4f6",
            textAlign: "center",
        }}>
            <div style={{ fontSize: "42px", fontWeight: 900, color: GREEN, lineHeight: 1, marginBottom: "8px" }}>
                {number}
            </div>
            <div style={{ fontSize: "13px", color: light ? "rgba(255,255,255,0.55)" : "#6b7280", lineHeight: 1.5 }}>
                {label}
            </div>
        </div>
    );
}

// ─── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, body }) {
    return (
        <div style={{
            padding: "28px", borderRadius: "16px",
            background: "#fff", border: "1px solid #f3f4f6",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>
            <div style={{
                width: "44px", height: "44px", borderRadius: "12px",
                background: "#f0fdf4", display: "flex", alignItems: "center",
                justifyContent: "center", marginBottom: "16px",
            }}>
                {icon}
            </div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827", marginBottom: "8px" }}>{title}</div>
            <div style={{ fontSize: "13.5px", color: "#6b7280", lineHeight: 1.7 }}>{body}</div>
        </div>
    );
}

// ─── Step ─────────────────────────────────────────────────────────────────────
function Step({ number, title, body }) {
    return (
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{
                width: "36px", height: "36px", borderRadius: "50%",
                background: GREEN + "18", border: `2px solid ${GREEN}40`,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, fontSize: "13px", fontWeight: 800, color: "#16a34a",
            }}>
                {number}
            </div>
            <div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827", marginBottom: "6px" }}>{title}</div>
                <div style={{ fontSize: "13.5px", color: "#6b7280", lineHeight: 1.7 }}>{body}</div>
            </div>
        </div>
    );
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icons = {
    cashback: (
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
        </svg>
    ),
    cart: (
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 5h14M10 20a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"/>
            <path d="M17 8l-4 4-2-2"/>
        </svg>
    ),
    timing: (
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
        </svg>
    ),
    check: (
        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    ),
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function CartAbandonmentComponent() {
    const navigate = useNavigate();

    useEffect(() => {
        sendSlackMessageTracked("Lead is visiting the Cart Abandonment page.", "#website-updates");
    }, []);

    return (
        <div style={{ fontFamily: font }}>

            {/* ── Hero ──────────────────────────────────────────────────────── */}
            <section style={{
                background: DARK,
                padding: "120px 24px 100px",
                position: "relative",
                overflow: "hidden",
            }}>
                {/* Radial glow */}
                <div style={{
                    position: "absolute", inset: 0, pointerEvents: "none",
                    background: "radial-gradient(ellipse 70% 50% at 50% -10%, rgba(34,197,94,0.18) 0%, transparent 65%)",
                }}/>

                <div style={{ maxWidth: "820px", margin: "0 auto", textAlign: "center", position: "relative" }}>
                    <div style={{
                        display: "inline-flex", alignItems: "center", gap: "8px",
                        background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)",
                        borderRadius: "999px", padding: "6px 16px", marginBottom: "28px",
                    }}>
                        <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: GREEN }}/>
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "#4ade80" }}>
                            Cart Recovery · Powered by Store Credit
                        </span>
                    </div>

                    <h1 style={{
                        fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 900,
                        color: "#fff", lineHeight: 1.08, marginBottom: "24px",
                        letterSpacing: "-0.02em",
                    }}>
                        70% of carts are abandoned.<br/>
                        <span style={{ color: GREEN }}>Win them back.</span>
                    </h1>

                    <p style={{
                        fontSize: "18px", color: "rgba(255,255,255,0.55)",
                        lineHeight: 1.7, maxWidth: "560px", margin: "0 auto 40px",
                    }}>
                        Shoppers who get cashback on their first order come back. Pabloo wires store credit directly into checkout so finishing a purchase pays them back.
                    </p>

                    <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                        <button
                            onClick={() => window.location.href = "https://app.pabloo.com/sign-up"}
                            style={{
                                padding: "14px 32px", fontSize: "14px", fontWeight: 700,
                                background: GREEN, color: "#fff", border: "none",
                                borderRadius: "10px", cursor: "pointer",
                                boxShadow: "0 4px 20px rgba(34,197,94,0.4)",
                                transition: "opacity 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                            Start free trial
                        </button>
                        <button
                            onClick={() => navigate("/plans")}
                            style={{
                                padding: "14px 32px", fontSize: "14px", fontWeight: 600,
                                background: "rgba(255,255,255,0.08)", color: "#fff",
                                border: "1px solid rgba(255,255,255,0.15)", borderRadius: "10px",
                                cursor: "pointer", transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.13)"}
                            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
                        >
                            View pricing
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Stats strip ───────────────────────────────────────────────── */}
            <section style={{ background: "#111", padding: "48px 24px" }}>
                <div style={{
                    maxWidth: "1080px", margin: "0 auto",
                    display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px",
                }}>
                    <StatCard number="70%" label="of carts never reach checkout" light />
                    <StatCard number="2.3×" label="checkout rate lift with cashback in the flow" light />
                    <StatCard number="35%+" label="second-order rate among Pabloo merchants" light />
                    <StatCard number="5×" label="what it costs to get a new customer vs. keeping one" light />
                </div>
            </section>

            {/* ── Problem ───────────────────────────────────────────────────── */}
            <Section bg="#f9fafb">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "center" }}>
                    <div>
                        <Eyebrow>The problem</Eyebrow>
                        <SectionHeading>You paid to get them to the cart. Most of them don't buy.</SectionHeading>
                        <Body>
                            For every 10 shoppers who add items to their cart, 7 leave without buying. Shipping surprises at checkout, account creation walls, and payment friction are the usual culprits.
                        </Body>
                        <Body>
                            <br/>You also paid to get those people to your site. A lost cart isn't just a missed sale, it's a wasted acquisition cost sitting on top of it.
                        </Body>
                    </div>

                    {/* Visual */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {[
                            { label: "Unexpected shipping costs", pct: 48 },
                            { label: "Just browsing / not ready", pct: 34 },
                            { label: "Found a better price elsewhere", pct: 27 },
                            { label: "Complicated checkout process", pct: 22 },
                            { label: "Payment friction / trust concerns", pct: 18 },
                        ].map(({ label, pct }) => (
                            <div key={label}>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12.5px", color: "#6b7280", marginBottom: "5px" }}>
                                    <span>{label}</span>
                                    <span style={{ fontWeight: 600, color: "#374151" }}>{pct}%</span>
                                </div>
                                <div style={{ height: "6px", background: "#e5e7eb", borderRadius: "999px", overflow: "hidden" }}>
                                    <div style={{ width: `${pct * 2}%`, height: "100%", background: `linear-gradient(90deg, #16a34a, ${GREEN})`, borderRadius: "999px", transition: "width 0.6s ease" }}/>
                                </div>
                            </div>
                        ))}
                        <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "8px" }}>Source: Baymard Institute</p>
                    </div>
                </div>
            </Section>

            {/* ── Solution ──────────────────────────────────────────────────── */}
            <Section bg="#fff">
                <div style={{ textAlign: "center", maxWidth: "580px", margin: "0 auto 56px" }}>
                    <Eyebrow center>The Pabloo approach</Eyebrow>
                    <SectionHeading center>Give shoppers a reason to finish the purchase.</SectionHeading>
                    <Body center>
                        Pabloo attaches store credit to the checkout experience so shoppers see something in it for them before they decide whether to leave.
                    </Body>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
                    <FeatureCard
                        icon={Icons.cashback}
                        title="Cashback at checkout"
                        body="Issue a percentage of the order back as store credit the moment the purchase is confirmed. It's in their wallet before they close the tab."
                    />
                    <FeatureCard
                        icon={Icons.cart}
                        title="Automated cart recovery"
                        body="Detect abandoned sessions and send targeted store credit offers to bring shoppers back. No blanket discounts that undercut your full-price catalog."
                    />
                    <FeatureCard
                        icon={Icons.timing}
                        title="Expiring cashback offers"
                        body="Set an expiration window on cashback. A deal that's gone by Friday gets more attention than one that's always available."
                    />
                </div>
            </Section>

            {/* ── How it works ──────────────────────────────────────────────── */}
            <Section bg="#f9fafb">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", alignItems: "center" }}>

                    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                        <div>
                            <Eyebrow>How it works</Eyebrow>
                            <SectionHeading>How it works.</SectionHeading>
                        </div>
                        <Step
                            number="1"
                            title="Shopper adds items but doesn't check out"
                            body="Pabloo detects the session and queues a store credit offer based on what's in the cart and what that customer has spent before."
                        />
                        <Step
                            number="2"
                            title="Cashback offer goes out before or after they leave"
                            body="A store credit offer shows up in checkout or goes out by email. The shopper sees something in it for them either way."
                        />
                        <Step
                            number="3"
                            title="Shopper completes the purchase"
                            body="Store credit lands in their wallet automatically. They see it next time they visit, which is usually enough to bring them back."
                        />
                        <Step
                            number="4"
                            title="You see what came back"
                            body="Your dashboard shows how many carts recovered, how much credit went out, and what it brought in."
                        />
                    </div>

                    {/* Mock dashboard card */}
                    <div style={{
                        background: "#fff", borderRadius: "20px",
                        boxShadow: "0 8px 40px rgba(0,0,0,0.08)",
                        border: "1px solid #f3f4f6",
                        overflow: "hidden",
                    }}>
                        <div style={{ background: "#0C3326", padding: "16px 20px" }}>
                            <div style={{ fontSize: "11px", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "4px" }}>Cart Recovery</div>
                            <div style={{ fontSize: "24px", fontWeight: 800, color: "#fff" }}>$14,280</div>
                            <div style={{ fontSize: "12px", color: "#4ade80" }}>↑ recovered this month</div>
                        </div>
                        <div style={{ padding: "20px" }}>
                            {[
                                { label: "Carts recovered", value: "148", trend: "+22%" },
                                { label: "Recovery rate", value: "31%", trend: "+8 pts" },
                                { label: "Avg order value", value: "$96.50", trend: "+14%" },
                                { label: "Credit issued", value: "$2,140", trend: "" },
                            ].map(row => (
                                <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f9fafb" }}>
                                    <span style={{ fontSize: "13px", color: "#6b7280" }}>{row.label}</span>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{row.value}</span>
                                        {row.trend && <span style={{ fontSize: "11px", fontWeight: 600, color: "#16a34a", background: "#f0fdf4", borderRadius: "999px", padding: "2px 8px" }}>{row.trend}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </Section>

            {/* ── Why store credit beats discounts ──────────────────────────── */}
            <Section bg={DARK}>
                <div style={{ textAlign: "center", maxWidth: "600px", margin: "0 auto 56px" }}>
                    <Eyebrow light>Store credit vs. discounts</Eyebrow>
                    <SectionHeading light center>Discounts train customers to wait for the next sale. Store credit doesn't.</SectionHeading>
                    <Body light center>
                        A sitewide discount hands money to customers who would have paid full price anyway. Store credit only gets spent when someone comes back.
                    </Body>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                    {[
                        {
                            label: "Sitewide discount",
                            dark: true,
                            points: [
                                "Cuts into margin on every order",
                                "Trained customers to wait for sales",
                                "No repeat-purchase incentive",
                                "Devalues your full-price products",
                            ],
                            bad: true,
                        },
                        {
                            label: "Pabloo store credit",
                            dark: false,
                            points: [
                                "Only redeemed when customer returns",
                                "Increases average order frequency",
                                "Customers feel rewarded, not discounted",
                                "Keeps revenue inside your store",
                            ],
                            bad: false,
                        },
                    ].map(col => (
                        <div key={col.label} style={{
                            padding: "28px", borderRadius: "16px",
                            background: col.bad ? "rgba(255,255,255,0.04)" : "rgba(34,197,94,0.1)",
                            border: col.bad ? "1px solid rgba(255,255,255,0.08)" : `1px solid ${GREEN}30`,
                        }}>
                            <div style={{ fontSize: "13px", fontWeight: 700, color: col.bad ? "#9ca3af" : "#4ade80", marginBottom: "20px" }}>
                                {col.label}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                {col.points.map(p => (
                                    <div key={p} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                                        {col.bad ? (
                                            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                                                <path d="M18 6L6 18M6 6l12 12"/>
                                            </svg>
                                        ) : (
                                            <span style={{ marginTop: "2px" }}>{Icons.check}</span>
                                        )}
                                        <span style={{ fontSize: "13.5px", color: col.bad ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>{p}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* ── Pricing ───────────────────────────────────────────────────── */}
            <ModernPricingView />

            {/* ── Final CTA ─────────────────────────────────────────────────── */}
            <Section bg="#f0fdf4" style={{ textAlign: "center" }}>
                <div style={{ maxWidth: "520px", margin: "0 auto" }}>
                    <Eyebrow center>Get started</Eyebrow>
                    <SectionHeading center>Set up your first campaign today.</SectionHeading>
                    <Body center>
                        Connect your Shopify store and your first cart recovery campaign goes live in under 10 minutes.
                    </Body>
                    <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "32px", flexWrap: "wrap" }}>
                        <button
                            onClick={() => window.location.href = "https://app.pabloo.com/sign-up"}
                            style={{
                                padding: "14px 32px", fontSize: "14px", fontWeight: 700,
                                background: "#111827", color: "#fff", border: "none",
                                borderRadius: "10px", cursor: "pointer", transition: "opacity 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.opacity = "0.82"}
                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                        >
                            Start free trial
                        </button>
                        <button
                            onClick={() => navigate("/bookCall")}
                            style={{
                                padding: "14px 32px", fontSize: "14px", fontWeight: 600,
                                background: "#fff", color: "#374151",
                                border: "1px solid #e5e7eb", borderRadius: "10px",
                                cursor: "pointer", transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                            onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        >
                            Talk to us
                        </button>
                    </div>
                    <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "16px" }}>
                        No credit card required · 14-day free trial · Cancel anytime
                    </p>
                </div>
            </Section>

        </div>
    );
}
